import {
  ButtonLayout,
  ControlsPosition,
  ImageFit,
  Layout,
  PlatformSettingsWeb,
  WebPlayer,
} from '../../api'

export class WebPlayerThemeUtil {
  static layoutClass(webPlayer: WebPlayer | PlatformSettingsWeb): string {
    switch (webPlayer.layoutConfiguration.layout) {
      case Layout.Column:
        return 'flex-column'
      case Layout.ColumnReverse:
        return 'flex-column-reverse'
      case Layout.Row:
        return 'flex-row'
      case Layout.RowReverse:
        return 'flex-row-reverse'
    }
    return ''
  }

  static displayClass(webPlayer: WebPlayer | PlatformSettingsWeb): string {
    switch (webPlayer.layoutConfiguration.imageFit) {
      case ImageFit.Contain:
        return 'image-contain'
      case ImageFit.Cover:
        return 'image-cover'
      case ImageFit.Fill:
        return 'image-fill'
      case ImageFit.None:
        return 'image-preserve'
    }
    return ''
  }

  static backgroundStyle(webPlayer: WebPlayer | PlatformSettingsWeb) {
    return {
      '--background-color': webPlayer.layoutConfiguration.backgroundColor,
      '--darkreader-bg--background-color':
        webPlayer.layoutConfiguration.backgroundColor,
    }
  }

  static controlsClass(webPlayer: WebPlayer | PlatformSettingsWeb) {
    if (
      webPlayer.layoutConfiguration.controlsPosition === ControlsPosition.Top
    ) {
      return 'top'
    } else {
      return 'bottom'
    }
  }

  static headerStyle(webPlayer: WebPlayer | PlatformSettingsWeb) {
    return {
      '--header-background': webPlayer.layoutConfiguration.headerColor,
      '--darkreader-bg--header-background':
        webPlayer.layoutConfiguration.headerColor,

      '--header-font-color': webPlayer.layoutConfiguration.headerFontColor,
      '--darkreader-text--header-font-color':
        webPlayer.layoutConfiguration.headerFontColor,

      '--background-color-hover': webPlayer.layoutConfiguration.accentColor,
      '--darkreader-bg--background-color-hover':
        webPlayer.layoutConfiguration.accentColor,

      '--border-color': webPlayer.layoutConfiguration.headerFontColor,
      '--darkreader-border--border-color':
        webPlayer.layoutConfiguration.headerFontColor,

      '--button-text-color-hover': webPlayer.layoutConfiguration.headerColor,
      '--darkreader-text--button-text-color-hover':
        webPlayer.layoutConfiguration.headerColor,

      '--seeker-bar-color': webPlayer.layoutConfiguration.accentColor,
      '--darkreader-bg--seeker-bar-color':
        webPlayer.layoutConfiguration.accentColor,
    }
  }

  static buttonStyle(webPlayer: WebPlayer | PlatformSettingsWeb) {
    return {
      '--color': webPlayer.buttonConfiguration.fontColor,
      '--darkreader-text--color': webPlayer.buttonConfiguration.fontColor,

      '--color-hover': webPlayer.buttonConfiguration.color,
      '--darkreader-text--color-hover': webPlayer.buttonConfiguration.color,

      '--background-color': webPlayer.buttonConfiguration.color,
      '--darkreader-bg--background-color': webPlayer.buttonConfiguration.color,

      '--background-color-hover': webPlayer.layoutConfiguration.accentColor,
      '--darkreader-bg--background-color-hover':
        webPlayer.layoutConfiguration.accentColor,

      '--border': `${webPlayer.buttonConfiguration.borderSize}px solid ${webPlayer.buttonConfiguration.borderColor}`,
      '--darkreader-border--border': `${webPlayer.buttonConfiguration.borderSize}px solid ${webPlayer.buttonConfiguration.borderColor}`,

      '--border-hover': `${webPlayer.buttonConfiguration.borderSize}px solid ${webPlayer.buttonConfiguration.fontColor}`,
      '--darkreader-border--border-hover': `${webPlayer.buttonConfiguration.borderSize}px solid ${webPlayer.buttonConfiguration.fontColor}`,

      '--border-radius': `${webPlayer.buttonConfiguration.borderRadius}px`,
      '--margin': `${webPlayer.buttonConfiguration.margin}px`,
      '--padding': `${webPlayer.buttonConfiguration.height}px ${webPlayer.buttonConfiguration.width}px`,
      '--font-family': webPlayer.buttonConfiguration.font,
      '--font-size': `${webPlayer.buttonConfiguration.fontSize}px`,
      '--font-style': webPlayer.buttonConfiguration.fontItalic
        ? 'italic'
        : 'normal',
      '--font-weight': webPlayer.buttonConfiguration.fontBold
        ? 'bold'
        : 'normal',
    }
  }

  static defaultUtteranceButtonStyle(
    webPlayer: WebPlayer | PlatformSettingsWeb
  ) {
    return {
      '--color': webPlayer.buttonConfiguration.fontColor,
      '--darkreader-text--color': webPlayer.buttonConfiguration.fontColor,

      '--color-hover': webPlayer.buttonConfiguration.color,
      '--darkreader-text--color-hover': webPlayer.buttonConfiguration.color,

      '--background-color': webPlayer.buttonConfiguration.color,
      '--darkreader-bg--background-color': webPlayer.buttonConfiguration.color,

      '--background-color-hover': webPlayer.layoutConfiguration.accentColor,
      '--darkreader-bg--background-color-hover':
        webPlayer.layoutConfiguration.accentColor,

      '--border': `${webPlayer.buttonConfiguration.borderSize}px solid ${webPlayer.buttonConfiguration.borderColor}`,
      '--darkreader-border--border': `${webPlayer.buttonConfiguration.borderSize}px solid ${webPlayer.buttonConfiguration.borderColor}`,

      '--border-hover': `${webPlayer.buttonConfiguration.borderSize}px solid ${webPlayer.buttonConfiguration.fontColor}`,
      '--darkreader-border--border-hover': `${webPlayer.buttonConfiguration.borderSize}px solid ${webPlayer.buttonConfiguration.fontColor}`,

      '--border-radius': `${webPlayer.buttonConfiguration.borderRadius}px`,
      '--margin': `${webPlayer.buttonConfiguration.margin}px`,

      '--padding': `5px`,
      '--font-family': webPlayer.buttonConfiguration.font,
      '--font-size': `12px`,
      '--font-style': 'normal',
    }
  }

  static getStartButtonStyle(webPlayer: WebPlayer | PlatformSettingsWeb) {
    return {
      '--color': webPlayer.buttonConfiguration.fontColor,
      '--darkreader-text--color': webPlayer.buttonConfiguration.fontColor,

      '--color-hover': webPlayer.buttonConfiguration.color,
      '--darkreader-text--color-hover': webPlayer.buttonConfiguration.color,

      '--background-color': webPlayer.buttonConfiguration.color,
      '--darkreader-bg--background-color': webPlayer.buttonConfiguration.color,

      '--background-color-hover': webPlayer.layoutConfiguration.accentColor,
      '--darkreader-bg--background-color-hover':
        webPlayer.layoutConfiguration.accentColor,
    }
  }

  static micButtonStyle(webPlayer: WebPlayer | PlatformSettingsWeb) {
    return {
      '--color': webPlayer.buttonConfiguration.fontColor,
      '--darkreader-text--color': webPlayer.buttonConfiguration.fontColor,

      '--color-hover': webPlayer.buttonConfiguration.color,
      '--darkreader-text--color-hover': webPlayer.buttonConfiguration.color,

      '--background-color': webPlayer.buttonConfiguration.color,
      '--darkreader-bg--background-color': webPlayer.buttonConfiguration.color,

      '--background-color-hover': webPlayer.layoutConfiguration.accentColor,
      '--darkreader-bg--background-color-hover':
        webPlayer.layoutConfiguration.accentColor,

      '--border': `${webPlayer.buttonConfiguration.borderSize}px solid ${webPlayer.buttonConfiguration.borderColor}`,
      '--darkreader-border--border': `${webPlayer.buttonConfiguration.borderSize}px solid ${webPlayer.buttonConfiguration.borderColor}`,

      '--border-hover': `${webPlayer.buttonConfiguration.borderSize}px solid ${webPlayer.buttonConfiguration.fontColor}`,
      '--darkreader-border--border-hover': `${webPlayer.buttonConfiguration.borderSize}px solid ${webPlayer.buttonConfiguration.fontColor}`,
    }
  }

  static buttonContainerStyle(webPlayer: WebPlayer | PlatformSettingsWeb) {
    return {
      '--flex-direction':
        webPlayer.buttonConfiguration.layout === ButtonLayout.Column
          ? 'column'
          : 'row',
      '--flex-wrap':
        webPlayer.buttonConfiguration.layout === ButtonLayout.RowWrap
          ? 'wrap'
          : 'nowrap',
    }
  }
}
